// Pin. The line's first move puts a slider (bishop, rook or queen) where it
// attacks an enemy piece with a more valuable enemy piece straight behind
// it on the same ray: the front piece cannot move off the ray without
// exposing the one behind. The front piece is not a pawn (a pinned pawn is
// no tactic), and the pinner is safe where it stands, so the pinned piece
// cannot simply take it. The variation names the piece behind: `king` is
// the absolute pin, else `queen` or `rook` (the front piece is worth less,
// so nothing smaller can stand behind).
import type { Motif } from '@chess/api-mistakes';
import type { TacticLine } from './line.ts';
import { moved, safe, throughPairs, VALUE } from './rays.ts';

export function pin(line: TacticLine): Motif | undefined {
  const first = line.pv[0];
  const m = first ? moved(line.board, first) : undefined;
  if (!m || !safe(m.after, m.to)) return undefined;
  const pins = throughPairs(m.after, m.to).filter(
    ({ front, behind }) =>
      front.colour === line.victim &&
      behind?.colour === line.victim &&
      front.kind !== 'pawn' &&
      front.kind !== 'king' &&
      VALUE[behind.kind] > VALUE[front.kind],
  );
  // The most valuable piece behind names the pin: the king over a queen.
  const best = pins.sort((a, b) => VALUE[b.behind!.kind] - VALUE[a.behind!.kind])[0];
  if (!best) return undefined;
  const variation = best.behind!.kind;
  return variation === 'king' || variation === 'queen' || variation === 'rook' ? { family: 'pin', variation } : undefined;
}
